import { usePublicEmbedConfig } from "@/hooks/use-public-embed-config";
import {
  LOCALE_LABELS,
  getChromeStrings,
  setEmbedLocale,
  useEmbedLocale,
} from "@/lib/embed-locale";
import { AgentIcon } from "@/components/agents/agent-icon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ChatBubbleHeaderProps {
  /** Agent whose embed config supplies the header text. */
  agentId?: string | null;
  /** Fallback title when no headerText is configured (agent display name). */
  fallbackTitle?: string | null;
  /** Agent avatar (image/emoji) shown next to the title in the bubble header. */
  agentAvatar?: string | null;
  /**
   * Whether to render the avatar beside the title. Suppressed in the empty
   * state (before the first message) because the welcome hero already shows a
   * large avatar there; only shown once the conversation has started.
   */
  showAvatar?: boolean;
  /**
   * Whether to offer the language toggle. Passed only in the pre-chat/empty
   * state: a visitor who picked the wrong language at the gate cannot read the
   * widget and refreshing does NOT re-open the gate (the locale is already
   * stored), so they need a way back before they commit to a conversation.
   * Once messages exist the toggle is dropped — switching then would leave the
   * transcript in the previous language.
   */
  showLanguageSwitcher?: boolean;
  /** Callback when the close button is clicked. */
  onClose: () => void;
}

/**
 * Minimal header shown in bubble embed mode.
 *
 * Displays the agent's **avatar** + `headerText` (falling back to the agent
 * name) and a close button. The language is chosen once at the pre-chat gate
 * and locked for the session, so no in-chat language switcher is rendered.
 * Deliberately does NOT render the workspace credits badge: bubble mode is the
 * anonymous-visitor surface, and workspace spend is internal information
 * (Phase 2 audit).
 */
export function ChatBubbleHeader({
  agentId,
  fallbackTitle,
  agentAvatar,
  showAvatar = true,
  showLanguageSwitcher = false,
  onClose,
}: ChatBubbleHeaderProps) {
  const { config } = usePublicEmbedConfig(agentId ?? null, Boolean(agentId));
  const locale = useEmbedLocale();
  const strings = getChromeStrings(locale);
  const title = config?.headerText || fallbackTitle || null;

  /** Languages this agent offers; fewer than two means nothing to switch to. */
  const languages = config?.embed?.languages ?? [];

  /**
   * The language the toggle moves to — the next one in the configured order,
   * wrapping around. With the usual two this is a straight A ⇄ B flip, and it
   * still behaves sensibly if an agent offers three or more.
   */
  const nextLocale =
    languages.length >= 2
      ? languages[
          (Math.max(0, languages.indexOf(locale)) + 1) % languages.length
        ] ?? null
      : null;

  return (
    <header
      data-testid="chat-bubble-header"
      className="flex shrink-0 items-center gap-2 overflow-hidden p-3"
    >
      {/* Leading spacer mirrors the trailing close button so the avatar + title stay centered */}
      <div className="w-8 shrink-0" />
      <div className="flex min-w-0 flex-1 items-center justify-center gap-2">
        {showAvatar && (agentAvatar || title) && (
          <AgentIcon
            avatar={agentAvatar}
            name={fallbackTitle || title || "Agent"}
            size="sm"
            rounded="full"
            className="shrink-0"
          />
        )}
        {title ? (
          <span className="max-w-full truncate text-sm font-semibold text-foreground">
            {title}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center justify-end gap-1">
        {/*
          Shows BOTH languages as "EN / ع" rather than just the target: a lone
          "ع" gives no clue what it does, whereas two scripts side by side read
          as a language choice without needing any word for "language" — which
          matters when the visitor cannot read the language on screen. The
          active one is emphasised, the other dimmed, so it also communicates
          the current state.

          dir="ltr" pins the order so the pair does not visually reverse when
          the widget flips to RTL; the control should look identical in both.
        */}
        {showLanguageSwitcher && nextLocale && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  dir="ltr"
                  onClick={() => setEmbedLocale(nextLocale)}
                  className="rounded px-1.5 py-1 text-xs font-semibold transition-colors hover:bg-muted"
                  aria-label={`${strings.switchLanguage} — ${LOCALE_LABELS[nextLocale]?.native ?? nextLocale}`}
                  data-testid="embed-language-switcher"
                >
                  <span className="text-foreground">
                    {LOCALE_LABELS[locale]?.short ?? locale.toUpperCase()}
                  </span>
                  <span className="mx-0.5 text-muted-foreground/50">/</span>
                  <span className="text-muted-foreground">
                    {LOCALE_LABELS[nextLocale]?.short ?? nextLocale.toUpperCase()}
                  </span>
                </button>
              </TooltipTrigger>
              {/*
                Names the destination language in its OWN script ("التبديل إلى
                العربية" / "Switch to English"), so the hint is readable to the
                visitor who needs it most — the one who cannot read the language
                currently on screen.
              */}
              <TooltipContent side="bottom">
                <p className="text-xs">
                  {getChromeStrings(nextLocale).switchLanguage} —{" "}
                  {LOCALE_LABELS[nextLocale]?.native ?? nextLocale}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <button
          onClick={onClose}
          className="p-1 hover:bg-muted rounded transition-colors"
          title={strings.closeChat}
        >
          <svg className="size-4 text-muted-foreground hover:text-foreground transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </header>
  );
}
